/**
 * RUN 37 — the day landmarks a long fixture list needs, in one place.
 *
 * Both appointment pages now put the WHOLE schedule in a single table that the Technical
 * Delegate scrolls, rather than a day at a time (module 01) or twenty rows at a time
 * (module 02). Two things make that readable instead of merely longer:
 *
 *  * **A separator row at every date boundary** — `Fri 18 Sep — Day 3` — so a scroll has
 *    landmarks rather than four hundred interchangeable rows.
 *  * **Landing on today.** The TD refreshes the page on a match morning and expects that
 *    morning's matches under his cursor. Yesterday is a scroll up, tomorrow a scroll down;
 *    neither is a click, and neither is lost.
 *
 * The two pages share this module rather than each growing their own copy, because the one
 * thing worse than two fixture lists is two fixture lists that disagree about which day is
 * today.
 *
 * **Nothing here reads the clock on its own.** `tournamentToday` takes the instant as an
 * argument, defaulted, exactly as `todayIso` on the Fixtures page does — that is the pattern
 * this codebase already uses to make time testable, and it is followed rather than replaced.
 *
 * **And nothing here reads the DEVICE's zone** (RUN 83) — see `tournamentToday`.
 */

import { useEffect, useRef } from "react";

import { tournamentDay } from "../../app/tournamentTime.js";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * `2026-09-18` → `Fri 18 Sep`.
 *
 * Parsed as UTC and read back as UTC, so the label a date carries never depends on where the
 * laptop thinks it is: a match day is a calendar day in the host city, not an instant. An
 * unparseable date is returned exactly as it came, because printing the string the API sent
 * is always more honest than printing a date nobody sent.
 */
export function dayLabel(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return `${WEEKDAYS[date.getUTCDay()] ?? ""} ${String(date.getUTCDate())} ${
    MONTHS[date.getUTCMonth()] ?? ""
  }`;
}

/**
 * Today, in the tournament's own zone, as the `YYYY-MM-DD` shape a fixture carries.
 *
 * The zone matters because the whole point of the landing rule is that a TD in the host city
 * lands on the day he is working. A TD whose laptop is still on European time would otherwise
 * be shown yesterday's matches for the first nine hours of every match day.
 *
 * `timezone` is the loaded tournament's own, so a build pointed at a competition somewhere else
 * lands on ITS days. It is the argument rather than a constant for that reason alone.
 *
 * **RUN 83 — `null` and an unrecognised zone fall back to the TOURNAMENT's clock, not the
 * device's.** The old fallback reached for `now.getDate()` and its siblings, reasoning by
 * analogy with `Shell.tsx`, and the analogy does not hold. The masthead clock is *labelled*:
 * when it cannot name the tournament's zone it drops the city from its caption and says plainly
 * that it is showing local time. This number carries no label at all — it is compared straight
 * against a fixture's `date`, which is a calendar day in the host city — so a device answer here
 * is not a disclosed approximation, it is a wrong day presented as the right one. It is also the
 * ORDINARY case, not the exotic one: the tournament record reaches this through a context that
 * is empty until the first fetch lands, and it was empty in the two panel tests that failed the
 * moment the suite stopped running on Tokyo time.
 */
export function tournamentToday(timezone: string | null, now: Date = new Date()): string {
  if (timezone !== null) {
    try {
      // `en-CA` because it formats as `2026-09-18` — the shape a fixture's `date` is in,
      // without a hand-rolled reassembly of three `Intl` parts.
      const formatted = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(now);
      if (/^\d{4}-\d{2}-\d{2}$/.test(formatted)) return formatted;
    } catch {
      /* An unrecognised zone. Fall through to the tournament's own. */
    }
  }
  return tournamentDay(now);
}

/** One date's worth of rows, in the order the list draws them. */
export interface DayGroup<T> {
  date: string;
  fixtures: T[];
}

/**
 * The rows, grouped into the days they are played on.
 *
 * Sorted by date first, because the list is now a scroll through a fortnight and a schedule
 * that arrived in any other order would put Thursday above Monday. Within a day the incoming
 * order is kept: that is the kick-off order the API already sends, and re-sorting it here
 * would be this module deciding something the server has decided.
 */
export function dayGroups<T extends { date: string }>(
  fixtures: readonly T[],
): DayGroup<T>[] {
  const byDate = new Map<string, T[]>();
  for (const fixture of fixtures) {
    const held = byDate.get(fixture.date);
    if (held === undefined) byDate.set(fixture.date, [fixture]);
    else held.push(fixture);
  }
  return [...byDate.keys()]
    .sort()
    .map((date) => ({ date, fixtures: byDate.get(date) ?? [] }));
}

/**
 * `2026-09-18` → `1`, the tournament's own day numbering.
 *
 * Counted over the WHOLE schedule rather than over what is on screen, so `Day 3` stays Day 3
 * when the competition tabs hide the two matches that were above it. A day the schedule does
 * not contain has no number, and the separator prints the date alone rather than a guess.
 */
export function dayNumbers(dates: readonly string[]): Map<string, number> {
  const ordered = [...new Set(dates)].sort();
  return new Map(ordered.map((date, index) => [date, index + 1]));
}

/**
 * The day the list should open at.
 *
 *  · today, when the tournament is playing today;
 *  · the next day that has matches, when today is a rest day or the tournament has not
 *    started — the TD's next piece of work is the honest destination;
 *  · `null` once every day is behind him, and the list opens at the top, which is where the
 *    tournament it is a record of begins.
 */
export function landingDay(days: readonly string[], today: string): string | null {
  const ordered = [...new Set(days)].sort();
  if (ordered.includes(today)) return today;
  return ordered.find((day) => day > today) ?? null;
}

/**
 * The day a SCROLLING LIST should be anchored at — {@link landingDay}, and the last day with
 * matches once every day is behind him.
 *
 * The difference is deliberate and is about two different questions. `landingDay` answers
 * *which day is the TD's next piece of work*, and `null` is the honest answer when there is
 * none: the Match Day dropdown on the TO Appointments page falls back to the first day of the
 * schedule, because that is where the tournament it is a record of begins.
 *
 * A LIST has no such fallback to make. `null` there means "leave the pane at the top", which
 * on the last afternoon of a fortnight puts the TD at Day 1 — a fortnight of scrolling away
 * from the only rows that can still change. The last day with matches is where the work
 * finished, so that is where the pane opens.
 *
 * Still `null` when there is no schedule at all: a pane with no rows has nothing to anchor to.
 */
export function anchorDay(days: readonly string[], today: string): string | null {
  const landing = landingDay(days, today);
  if (landing !== null) return landing;
  const ordered = [...new Set(days)].sort();
  return ordered[ordered.length - 1] ?? null;
}

/**
 * Put the chosen day at the top of ITS OWN container, once, when the rows first arrive.
 *
 * Three properties this hook exists to guarantee:
 *
 *  * **Only the container moves.** `scrollTo` on the pane, never `scrollIntoView` on the row
 *    and never anything on the window: a list that dragged the whole page down to itself on
 *    every mount would take the masthead and the day-warning counts off screen.
 *  * **Once.** It is a landing position, not a leash. The TD scrolls to yesterday and it
 *    stays at yesterday; a redraw after an edit does not yank him back.
 *  * **It says where it went.** The container carries `data-scrolled-to` afterwards, which is
 *    what a test asserts against — the pixel offset of a row is zero in jsdom and therefore
 *    proves nothing about which day was chosen.
 *
 * `null` for `day` means "leave it at the top", which is already where a fresh container is,
 * so nothing is scrolled and nothing is stamped.
 *
 * "Once" is counted per ANSWER, not per mount: on the TO page the whole schedule arrives in a
 * second request a moment after the day does, and the landing day can therefore change from
 * "nothing to land on" to a real date while the TD is still looking at the first paint. A
 * hook that had already spent its one chance on the narrower list would leave him at the top
 * of a fortnight. It re-lands when, and only when, that answer actually changes.
 *
 * **`viewKey` widens "that answer" to include WHICH ROWS are on the table.** A competition or
 * stage filter removes rows from above the anchor without changing which day the anchor is —
 * so every row below it moves up the pane by however many rows went, and the day the TD was
 * reading slides off the top or down the middle. The pane is therefore re-landed whenever the
 * caller says its contents have been reshaped, and the caller passes the filters themselves so
 * that nothing else — a redraw after an edit, a new warning — can be mistaken for one. It is
 * empty by default, which is exactly the old behaviour for a caller that has no filters.
 */
export function useLandOnDay(
  container: React.RefObject<HTMLElement | null>,
  day: string | null,
  viewKey = "",
): void {
  const landed = useRef<string | undefined>(undefined);
  // ` ` cannot occur in a date or in a filter value, so no pair of (viewKey, day) can
  // collide with another by running together.
  const token = `${viewKey} ${day ?? ""}`;

  useEffect(() => {
    if (landed.current === token) return;
    const pane = container.current;
    if (pane === null) return;
    // No rows yet — the panel is still loading, and the effect will run again when they land.
    if (pane.querySelector("[data-day]") === null) return;

    landed.current = token;
    if (day === null) return;

    const row = pane.querySelector<HTMLElement>(`[data-day="${day}"]`);
    if (row === null) return;

    // Rects, not `offsetTop`: the pane is `position: static`, so `offsetTop` is measured
    // against the TABLE, not the pane, and landed the day 205px short on the built page
    // (found on the live site, 20 Sep). Rects are relative to the viewport for both elements,
    // so their difference plus the pane's current scroll is the row's true content offset —
    // and in jsdom every rect is 0, so `top` is the (zero) scrollTop, as harmless as before.
    const top = Math.max(
      0,
      pane.scrollTop + row.getBoundingClientRect().top - pane.getBoundingClientRect().top,
    );
    if (typeof pane.scrollTo === "function") pane.scrollTo({ top });
    else pane.scrollTop = top;
    pane.dataset["scrolledTo"] = day;
  });
}
